import React, { useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { config } from '../config';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import { FaCalendarAlt, FaClock, FaSearch, FaArrowRight } from 'react-icons/fa';
import { initFacebookPixel, trackEvent } from '../pixel';
import { PixelContext } from './Context/PixelContext';

const apiUrl = config.apiUrl;
const imageUrl = config.imageUrl;

const BlogArchive = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const { pixel } = useContext(PixelContext);
    const pixelId = pixel?.pixel_id || null;
    const pixelInitialized = useRef(false);

    useEffect(() => {
        if (pixelId && !pixelInitialized.current ) {
            pixelInitialized.current = true;
            initFacebookPixel(pixelId);
            trackEvent('ContentView');
        }
    }, [pixelId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${apiUrl}/blogs`);
                const posts = response.data.blogs;
                const featuredPosts = posts.filter(post => post.is_featured === 1);
                setPosts(featuredPosts);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filteredPosts = posts.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <>
            <Navbar />
            
            <motion.main
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="min-h-screen bg-gray-50"
            >
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16 pt-32">
                    <div className="container mx-auto px-4 text-center">
                        <motion.h1
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="text-4xl md:text-5xl font-bold mb-6"
                        >
                            Our Latest Articles
                        </motion.h1>
                        <motion.p
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-xl max-w-2xl mx-auto mb-8"
                        >
                            Discover insightful content to boost your knowledge
                        </motion.p>
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="max-w-md mx-auto relative"
                        >
                            <input
                                type="text"
                                placeholder="Search articles..."
                                className="w-full py-3 px-4 pr-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 focus:outline-none focus:ring-2 focus:ring-white"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <FaSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white" />
                        </motion.div>
                    </div>
                </div>

                {/* Blog Posts Section */}
                <div className="container mx-auto px-4 py-16">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                        </div>
                    ) : filteredPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post, index) => (
                                <motion.div
                                    key={post.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ y: -5 }}
                                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                                >
                                    <Link to={`/blog/${post.slug}`} className="block">
                                        <div className="relative h-60 overflow-hidden">
                                            <img
                                                src={`${imageUrl}/${post.image}`}
                                                alt={post.title}
                                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                                        </div>
                                    </Link>
                                    <div className="p-6">
                                        <div className="flex items-center text-sm text-gray-500 mb-3">
                                            <span className="flex items-center mr-4">
                                                <FaCalendarAlt className="mr-2 text-green-500" />
                                                {formatDate(post.created_at)}
                                            </span>
                                            <span className="flex items-center">
                                                <FaClock className="mr-2 text-green-500" />
                                                {post.read_time || '5'} min read
                                            </span>
                                        </div>
                                        <Link to={`/blog/${post.slug}`}>
                                            <h3 className="text-xl font-bold text-gray-800 mb-3 hover:text-green-600 transition-colors">
                                                {post.title}
                                            </h3>
                                        </Link>
                                        <p className="text-gray-600 mb-4 line-clamp-3">
                                            {post.content.replace(/<[^>]+>/g, '').substring(0, 150)}...
                                        </p>
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            className="inline-flex items-center text-green-600 font-medium hover:text-green-700"
                                        >
                                            Read more <FaArrowRight className="ml-2" />
                                        </Link>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                            <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">No articles found</h3>
                            <p className="text-gray-600 max-w-md mx-auto">
                                {searchTerm ? 'Try a different search term' : 'No featured articles available yet'}
                            </p>
                        </div>
                    )}
                </div>
            </motion.main>
            
            <Footer />
        </>
    );
};

export default BlogArchive;